import React from 'react';
import {connect} from 'react-redux';
import {FormRenderProps} from 'react-final-form';
import {FormLabel, Grid, InputAdornment, Typography} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import {createResource} from 'spectra-logic-ui/actions';
import {FormDialog, SingleSelect, TextField, Tooltip} from 'spectra-logic-ui/components';
import {Dispatch} from 'spectra-logic-ui';

import {AddIAMAccount} from '@/help';
import {LifecycleV2} from '@/types';
import Radio from '@/components/radio';
import {FormValues} from '@/accounts/form/types';
import {AccountType} from '@/accounts/form/enum';
import {validateAccount} from '@/accounts/form/validate';

type Props = {
  lifecycles: LifecycleV2[];
  onSubmit: (values: FormValues) => Promise<any>;
}

const useStyles = makeStyles({
  accountType: {
    display: 'inline-block',
  },
  status: {
    display: 'inline-block',
  },
});

export const StatusTooltip = () => {
  return (
    <Typography variant='body2' display='inline'>
      Account status controls the status of all access keys associated with all IAM users within the account.
      Inactive accounts will result in all access keys also being inactive, thus preventing those IAM users
      from accessing the system. Access keys cannot be activated while an account is inactive.
    </Typography>
  );
};

export const ConnectionLimitTooltip = 'Set the connection limit to limit the number of connections ' +
    'that can be used by IAM users within this account. This is a percentage of the total system connections. ' +
    'An empty value, which is the default, is the same as 100%. Connections can be overprovisioned.';

const CreateAccountDialog = ({lifecycles, onSubmit, ...otherProps}: Props) => {
  const initialValues = {accountType: AccountType.AWS, inactive: 'false'};
  const classes = useStyles();

  return (
    <FormDialog
      title='Add IAM Account' getHelpLocation={() => AddIAMAccount} onSubmit={onSubmit}
      initialValues={initialValues} validate={validateAccount} {...otherProps}
    >
      {({values}: FormRenderProps) => (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Radio className={classes.accountType}>
              <Radio.Button name='accountType' label='AWS' value={AccountType.AWS} />
              <Radio.Button name='accountType' label='Local' value={AccountType.LOCAL} />
            </Radio>
          </Grid>
          <Grid item xs={12}>
            <TextField name='description' label='Description' />
          </Grid>
          {values.accountType === AccountType.AWS &&
            <>
              <Grid item xs={12}>
                <TextField name='roleArn' label='Role ARN' />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name='externalId'
                  label='External ID'
                  helperText='Optional. Specifies who can assume the role.'
                />
              </Grid>
            </>
          }
          <Grid item xs={12}>
            <TextField name='email' label='Email' />
          </Grid>
          <Grid item xs={6}>
            <FormLabel>Status</FormLabel><Tooltip><StatusTooltip /></Tooltip>
            <Radio className={classes.status}>
              <Radio.Button name='inactive' label='Active' value='false' />
              <Radio.Button name='inactive' label='Inactive' value='true' />
            </Radio>
          </Grid>
          <Grid item xs={6}>
            <TextField
              parse={(value) => {
                const number = Number(value);
                if (!value && value !== '0') {
                  return null;
                } else if (isNaN(number)) {
                  return value;
                } else {
                  return number;
                }
              }}
              name='connectionLimit'
              label='Connection Limit'
              InputProps={{endAdornment: <InputAdornment position='end'>%</InputAdornment>}}
              tooltip={ConnectionLimitTooltip}
            />
          </Grid>
          <Grid item xs={12}>
            <SingleSelect
              name='defaultLifecycleId' label='Default Lifecycle' noneOption={'Use global default'}
              options={lifecycles.map((l) => ({key: l.id, text: l.name}))}
            />
          </Grid>
        </Grid>
      )}
    </FormDialog>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onSubmit: (values: FormValues) => {
    const {accountType, ...props} = values;
    if (accountType === AccountType.AWS) {
      props.local = false;
    } else {
      props.local = true;
      delete props.roleArn;
      delete props.externalId;
    }
    props.inactive = props.inactive === 'true';
    return dispatch(createResource('accounts', props));
  },
});

export default connect(undefined, mapDispatchToProps)(CreateAccountDialog);
